import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Modal component to display translation validation results and confirmation for import
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onConfirm - Function to call when confirming the import
 * @param {Object} props.validationResult - Validation result data
 * @param {boolean} props.isImporting - Whether an import is in progress
 * @param {Object} props.importResult - Result of the import operation
 */
const ValidationSummaryModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  validationResult,
  isImporting = false,
  importResult = null
}) => {
  const { t } = useTranslation();
  
  // State for pagination
  const [currentPageNew, setCurrentPageNew] = useState(1);
  const [currentPageChanged, setCurrentPageChanged] = useState(1);
  const [currentPageUnchanged, setCurrentPageUnchanged] = useState(1);
  
  const ITEMS_PER_PAGE = 15;
  
  // Reset pagination when modal opens or data changes
  useEffect(() => {
    if (isOpen) {
      setCurrentPageNew(1);
      setCurrentPageChanged(1);
      setCurrentPageUnchanged(1);
    }
  }, [isOpen, validationResult]);
  
  // Process validation data for display
  const processedData = useMemo(() => {
    // Add debugging for the validationResult
    console.log('Processing validation result:', validationResult);
    
    // Check if we need to normalize the data structure
    // The backend might return data in different formats:
    // 1. As a direct array of items (validationResult is the array itself)
    // 2. As a direct array in validationResult.data
    // 3. As translationItems at root or in summary
    
    // Handle the case where validationResult is directly the array
    const directArray = Array.isArray(validationResult) ? validationResult : null;
    
    // Next, check if validationResult is an object with a data property that's an array
    const dataFromRoot = Array.isArray(validationResult?.data) ? validationResult.data : null;
    
    // Check for translationItems at root or in summary
    const translationItemsFromRoot = Array.isArray(validationResult?.translationItems) ? validationResult.translationItems : null;
    const translationItemsFromSummary = Array.isArray(validationResult?.summary?.translationItems) ? validationResult.summary.translationItems : null;
    
    // Use the first available data source
    const items = directArray || dataFromRoot || translationItemsFromRoot || translationItemsFromSummary;
    
    // If no items array found in any location, return null
    if (!items || items.length === 0) {
      console.log('No translation items found in validation result:', validationResult);
      return null;
    }
    
    console.log('Found items array with length:', items.length);
    
    // Group translations by item
    const itemsMap = {};
    const languageSet = new Set();
    
    // First pass to group items and collect all languages
    items.forEach(item => {
      // Initialize the item if we haven't seen it yet
      if (!itemsMap[item.id]) {
        itemsMap[item.id] = {
          id: item.id,
          referenceName: item.referenceName,
          type: item.type,
          translations: {}
        };
      }
      
      // Add translations to the item
      item.translations.forEach(translation => {
        languageSet.add(translation.language_code);
        
        // Extract the actual value, which might be in either 'value' or 'name' field
        const actualValue = translation.value !== undefined ? translation.value : translation.name;
        
        // Determine if the translation was actually modified
        // Only consider it modified if existing value exists and is different from the new value
        const isModified = translation.existing !== undefined && 
                          actualValue !== translation.existing &&
                          translation.operation === 'update';

        itemsMap[item.id].translations[translation.language_code] = {
          value: actualValue, // Use the normalized value
          existing: translation.existing,
          operation: translation.operation,
          language: translation.language_name || translation.language,
          isModified: isModified
        };
      });
    });
    
    // Convert to arrays and sort
    const processedItems = Object.values(itemsMap);
    const languages = Array.from(languageSet);
    
    // Separate items by translation status
    const itemsWithNewTranslations = processedItems.filter(item => 
      Object.values(item.translations).some(t => t.operation === 'create')
    );
    
    // Items with updates where at least one translation actually changed
    const itemsWithChangedTranslations = processedItems.filter(item =>
      Object.values(item.translations).some(t => 
        t.operation === 'update' && t.isModified === true
      )
    );
    
    // Items where all updates have no actual changes
    const itemsWithUnchangedTranslations = processedItems.filter(item => {
      // Has at least one 'update' operation
      const hasUpdates = Object.values(item.translations).some(t => t.operation === 'update');
      // No 'create' operations
      const hasNoCreates = !Object.values(item.translations).some(t => t.operation === 'create');
      // No modifications in any of the updates
      const hasNoModifications = !Object.values(item.translations).some(t => t.isModified === true);
      
      return hasUpdates && hasNoCreates && hasNoModifications;
    });
    
    console.log('Processed validation data:', {
      totalItems: processedItems.length,
      languages,
      newItems: itemsWithNewTranslations.length,
      changedItems: itemsWithChangedTranslations.length,
      unchangedItems: itemsWithUnchangedTranslations.length
    });
    
    return {
      languages,
      items: processedItems,
      itemsWithNewTranslations,
      itemsWithChangedTranslations,
      itemsWithUnchangedTranslations
    };
  }, [validationResult]);
  
  // Ensure proper summary properties even if structure is different
  const summary = useMemo(() => {
    if (!validationResult) return null;
    
    // Handle the case where properties are at different levels
    // Check for direct properties first, then summary, then calculate from processed data
    const result = {
      totalItems: validationResult.totalItems || validationResult.summary?.totalItems || processedData?.items?.length || 0,
      totalTranslations: validationResult.totalTranslations || validationResult.summary?.totalTranslations || 
        (processedData?.items?.reduce((sum, item) => sum + Object.keys(item.translations).length, 0) || 0),
      createCount: validationResult.createCount || validationResult.summary?.createCount || 
        (processedData?.itemsWithNewTranslations?.reduce((sum, item) => 
          sum + Object.values(item.translations).filter(t => t.operation === 'create').length, 0) || 0),
      updateCount: validationResult.updateCount || validationResult.summary?.updateCount || processedData?.itemsWithChangedTranslations?.length || 0,
      errors: validationResult.errors || validationResult.summary?.errors || []
    };
    
    if (validationResult.valid !== undefined) {
      result.valid = validationResult.valid;
    } else if (validationResult.summary?.valid !== undefined) {
      result.valid = validationResult.summary.valid;
    }
    
    return result;
  }, [validationResult, processedData]);
  
  // Calculate paginated data
  const paginatedNew = useMemo(() => {
    if (!processedData?.itemsWithNewTranslations) return { items: [], totalPages: 0 };
    const start = (currentPageNew - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const items = processedData.itemsWithNewTranslations.slice(start, end);
    const totalPages = Math.ceil(processedData.itemsWithNewTranslations.length / ITEMS_PER_PAGE);
    return { items, totalPages };
  }, [processedData, currentPageNew]);

  const paginatedChanged = useMemo(() => {
    if (!processedData?.itemsWithChangedTranslations) return { items: [], totalPages: 0 };
    const start = (currentPageChanged - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const items = processedData.itemsWithChangedTranslations.slice(start, end);
    const totalPages = Math.ceil(processedData.itemsWithChangedTranslations.length / ITEMS_PER_PAGE);
    return { items, totalPages };
  }, [processedData, currentPageChanged]);

  const paginatedUnchanged = useMemo(() => {
    if (!processedData?.itemsWithUnchangedTranslations) return { items: [], totalPages: 0 };
    const start = (currentPageUnchanged - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const items = processedData.itemsWithUnchangedTranslations.slice(start, end);
    const totalPages = Math.ceil(processedData.itemsWithUnchangedTranslations.length / ITEMS_PER_PAGE);
    return { items, totalPages };
  }, [processedData, currentPageUnchanged]);
  
  if (!isOpen) return null;
  
  // Handle confirmed import
  const handleImport = () => {
    // Make sure we have validation data and there are no errors
    if (!validationResult || (summary?.errors && summary.errors.length > 0)) {
      console.log('Cannot import: validation failed or has errors', {
        hasValidationResult: !!validationResult,
        errorsLength: summary?.errors?.length
      });
      return;
    }
    
    // Make sure we're not already importing and there's no existing import result
    if (isImporting || importResult) {
      console.log('Cannot import: already importing or import complete', {
        isImporting,
        hasImportResult: !!importResult
      });
      return;
    }
    
    // Make sure the onConfirm handler exists
    if (onConfirm) {
      console.log('Calling onConfirm handler to start import');
      onConfirm();
    } else {
      console.log('No onConfirm handler provided');
    }
  };
  
  // Handle modal close
  const handleClose = () => {
    if (!isImporting) {
      onClose();
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Background overlay */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
      
      {/* Modal content */}
      <div className="flex items-end sm:items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          {/* Modal header */}
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              {/* Icon: document check */}
              <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                {importResult 
                  ? t('translations.importResults') 
                  : t('translations.validationResults')}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {importResult 
                    ? t('translations.importSummary') 
                    : t('translations.validationSummary')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Modal body */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            {importResult ? (
              // Import results
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-100 rounded-md p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        {t('translations.importSuccess')}
                      </h3>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-md p-4">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">
                        {t('translations.itemsProcessed')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {importResult?.stats?.itemsProcessed ?? 0}
                      </dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">
                        {t('translations.totalTranslations')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {(importResult?.stats?.translationsCreated ?? 0) + (importResult?.stats?.translationsUpdated ?? 0)}
                      </dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">
                        {t('translations.translationsCreated')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {importResult?.stats?.translationsCreated ?? 0}
                      </dd>
                    </div>
                    
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">
                        {t('translations.translationsUpdated')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {importResult?.stats?.translationsUpdated ?? 0}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            ) : (
              // Validation results
              <div className="space-y-3">
                {validationResult && (
                  <div className="bg-gray-50 rounded-md p-4">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                          {t('translations.totalItems')}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {summary.totalItems}
                        </dd>
                      </div>
                      
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                          {t('translations.totalTranslations')}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {summary.totalTranslations}
                        </dd>
                      </div>
                      
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                          {t('translations.toBeCreated')}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {summary.createCount}
                        </dd>
                      </div>
                      
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                          {t('translations.toBeUpdated')}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {processedData?.itemsWithChangedTranslations?.length || 0}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
                
                {/* Validation warnings if any */}
                {summary?.errors && summary.errors.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-100 rounded-md p-3">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          {t('translations.warningsFound', { count: summary.errors.length })}
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <ul className="list-disc pl-5 space-y-1">
                            {summary.errors.slice(0, 5).map((error, index) => (
                              <li key={index}>
                                {t('translations.rowError', { row: error.row })}: {error.message}
                              </li>
                            ))}
                            {summary.errors.length > 5 && (
                              <li>
                                {t('translations.moreErrors', { count: summary.errors.length - 5 })}
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* New translations table */}
                {paginatedNew?.items?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {t('translations.newTranslationsHeading')}
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                      <div className="overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                          <div className="max-h-96 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                  {processedData.languages.map(lang => (
                                    <th key={lang} scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t(`languageNames.${lang}`, lang)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedNew.items.map(item => (
                                  <tr key={item.id}>
                                    {processedData.languages.map(lang => {
                                      const translation = item.translations[lang];
                                      
                                      return (
                                        <td key={`${item.id}-${lang}`} className="px-3 py-2 text-xs break-words">
                                          {translation ? (
                                            <div 
                                              className={`px-2 py-1 rounded ${
                                                translation.operation === 'create' 
                                                  ? 'bg-green-50 text-green-700' 
                                                  : translation.isModified === true
                                                    ? 'bg-yellow-50 text-yellow-700'
                                                    : 'bg-blue-50 text-blue-700'
                                              }`}
                                              style={{ maxWidth: '100%', wordBreak: 'break-word' }}
                                            >
                                              {translation.value || <span className="italic text-gray-400">{t('translations.notSet')}</span>}
                                              {translation.isModified === true && translation.existing && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                  {t('translations.from')}: {translation.existing}
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-gray-400">—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    {paginatedNew.totalPages > 1 && (
                      <PaginationControls 
                        currentPage={currentPageNew}
                        totalPages={paginatedNew.totalPages}
                        onPageChange={setCurrentPageNew}
                        totalItems={processedData.itemsWithNewTranslations.length}
                      />
                    )}
                  </div>
                )}
                
                {/* Updated translations table - only show items with actual changes */}
                {paginatedChanged?.items?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {t('translations.updatedTranslationsHeading')}
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                      <div className="overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                          <div className="max-h-96 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                  {processedData.languages.map(lang => (
                                    <th key={lang} scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t(`languageNames.${lang}`, lang)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedChanged.items.map(item => (
                                  <tr key={item.id}>
                                    {processedData.languages.map(lang => {
                                      const translation = item.translations[lang];
                                      
                                      return (
                                        <td key={`${item.id}-${lang}`} className="px-3 py-2 text-xs break-words">
                                          {translation ? (
                                            <div 
                                              className={`px-2 py-1 rounded ${
                                                translation.isModified
                                                  ? 'bg-yellow-50 text-yellow-700'
                                                  : 'bg-blue-50 text-blue-700'
                                              }`}
                                              style={{ maxWidth: '100%', wordBreak: 'break-word' }}
                                            >
                                              {translation.value || <span className="italic text-gray-400">{t('translations.notSet')}</span>}
                                              {translation.isModified === true && translation.existing && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                  {t('translations.from')}: {translation.existing}
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-gray-400">—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    {paginatedChanged.totalPages > 1 && (
                      <PaginationControls 
                        currentPage={currentPageChanged}
                        totalPages={paginatedChanged.totalPages}
                        onPageChange={setCurrentPageChanged}
                        totalItems={processedData.itemsWithChangedTranslations.length}
                      />
                    )}
                  </div>
                )}
                
                {/* Unchanged translations table - for items with no actual changes */}
                {paginatedUnchanged?.items?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {t('translations.unchangedTranslationsHeading', { defaultValue: 'Items with Unchanged Translations' })}
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                      <div className="overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                          <div className="max-h-96 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                  {processedData.languages.map(lang => (
                                    <th key={lang} scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {t(`languageNames.${lang}`, lang)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedUnchanged.items.map(item => (
                                  <tr key={item.id}>
                                    {processedData.languages.map(lang => {
                                      const translation = item.translations[lang];
                                      
                                      return (
                                        <td key={`${item.id}-${lang}`} className="px-3 py-2 text-xs break-words">
                                          {translation ? (
                                            <div 
                                              className="px-2 py-1 rounded bg-blue-50 text-blue-700"
                                              style={{ maxWidth: '100%', wordBreak: 'break-word' }}
                                            >
                                              {translation.value || <span className="italic text-gray-400">{t('translations.notSet')}</span>}
                                            </div>
                                          ) : (
                                            <span className="text-gray-400">—</span>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    {paginatedUnchanged.totalPages > 1 && (
                      <PaginationControls 
                        currentPage={currentPageUnchanged}
                        totalPages={paginatedUnchanged.totalPages}
                        onPageChange={setCurrentPageUnchanged}
                        totalItems={processedData.itemsWithUnchangedTranslations.length}
                      />
                    )}
                  </div>
                )}
                
                {/* Validation success message */}
                {validationResult && summary?.totalItems > 0 && (!summary.errors || summary.errors.length === 0) && (
                  <div className="bg-green-50 border border-green-100 rounded-md p-3 mt-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          {t('translations.fileValid')}
                        </h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p>
                            {t('translations.readyToImport')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {isImporting && (
              <div className="flex justify-center items-center my-4">
                <svg className="animate-spin h-5 w-5 text-blue-500 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('translations.importing')}</span>
              </div>
            )}
          </div>
          
          {/* Modal footer */}
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            {!importResult && summary?.totalItems > 0 && (!summary.errors || summary.errors.length === 0) && (
              <button
                type="button"
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${
                  isImporting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleImport}
                disabled={isImporting || (!validationResult?.validationId && !validationResult?.valid)}
              >
                {isImporting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('translations.importing')}
                  </>
                ) : (
                  t('translations.confirmImport')
                )}
              </button>
            )}
            
            <button
              type="button"
              className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm ${
                isImporting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={handleClose}
              disabled={isImporting}
            >
              {importResult ? t('common.close') : t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Pagination Controls Component
const PaginationControls = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  const { t } = useTranslation();
  const ITEMS_PER_PAGE = 15; // Ensure this matches the modal's setting

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(startItem + ITEMS_PER_PAGE - 1, totalItems);

  return (
    <nav
      className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-2"
      aria-label="Pagination"
    >
      <div className="hidden sm:block">
        <p className="text-sm text-gray-700">
          {t('pagination.showing')} <span className="font-medium">{startItem}</span> {t('pagination.to')} <span className="font-medium">{endItem}</span> {t('pagination.of')} <span className="font-medium">{totalItems}</span> {t('pagination.results')}
        </p>
      </div>
      <div className="flex flex-1 justify-between sm:justify-end">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('pagination.previous')}
        </button>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('pagination.next')}
        </button>
      </div>
    </nav>
  );
};

export default ValidationSummaryModal; 